import React, { useState, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { add, isAfter, isBefore, startOfDay, format, formatDistanceToNowStrict } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import _ from 'lodash';

import './CalendarEvents.scss';
import Icon from '../../Icon/Icon';
import { RootState } from '../../../configs/store';
import { getFlag } from '../../../utils/utils';
import customLocale from '../../../configs/date-fns';
import BottomMenuContext from '../../BottomMenu/Context/BottomMenu.context';

interface CalendarEventsProps {
  event: {
    code: number,
    countryCode: string,
    name: string,
    total: number
    tournaments: {
      eventStart: string,
      sportCode: any
    }
  },
  eventType: string,
  isSameDay?: boolean,
  openEvents?: boolean,
  calendarType: string
  setOpenEvents?: Function
}

function CalendarEvents({ event, eventType, isSameDay, openEvents, calendarType, setOpenEvents = () => {} }: CalendarEventsProps) {
  const [isOpen, setIsOpen] = useState(true);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { handleOpen } = useContext(BottomMenuContext);
  const { selectedCountries } = useSelector((state: RootState) => state.filters);

  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    setIsOpen(openEvents || selectedCountries.includes(event.code));
  }, [openEvents, selectedCountries]);

  const handleOnClick = (e) => {
    e.preventDefault();

    const index = _.indexOf(selectedCountries, event.code);
    let countries = JSON.parse(JSON.stringify(selectedCountries));
    setOpenEvents(false);
    if (index > -1) {
      countries = _.filter(countries, (country) => country !== event.code);
    } else {
      countries.push(event.code);
    }
    dispatch.filters.updateSelectedCountries(countries);
  };

  const handleOpenEvent = (code, provider) => (e) => {
    e.preventDefault();
    const path = eventType === 'racing' ? 'racing' : 'lotto';
    handleOpen && handleOpen('');
    history.push(`/${ path }/${ code }/${ provider }${ location.search }`);

    //clear search bar
    dispatch.events.getSportSearchResults('');
    dispatch.events.changeSearch('');
  };

  return (
    <>
      <h2
        className={
          classNames({
            'calendar__country': true,
            'calendar__country--closed': !isOpen
          })
        }
        onClick={ handleOnClick }
      >
        <span
          className="calendar__flag"
          style={ { backgroundImage: `url(${ getFlag(event.countryCode) })` } }
        ></span>
        { event.name }
        <span className="calendar__total">{ event.total }</span>
        <Icon icon="arrow" />
      </h2>
      <ul className={
        classNames({
          'calendar__list': true,
          'calendar__list--closed': !isOpen
        })
      }
      >
        {
          _.map(event.tournaments, (tournament) => {
            const eventStart = tournament.eventStart * 1000;
            const date = utcToZonedTime(eventStart, selectedTimezone);
            let formattedDate = format(date, 'HH:mm');
            if (isBefore(eventStart, add(new Date(), { hours: 1 })) && !isBefore(eventStart, new Date())) {
              formattedDate = formatDistanceToNowStrict(eventStart, { unit: 'minute', locale: customLocale });
            } else if (isAfter(startOfDay(eventStart), startOfDay(new Date())) && isSameDay) {
              formattedDate = format(eventStart, 'MM/dd HH:mm');
            } else if (isBefore(eventStart, new Date())){
              formattedDate = '- ' + formatDistanceToNowStrict(eventStart, { unit: 'minute', locale: customLocale, addSuffix: false });
            }

            return (
              <li
                className={
                  classNames({
                    'calendar__item--horse ': tournament.sportCode === 201 || tournament.sportCode === 202,
                    'calendar__item--dog': tournament.sportCode === 204,
                    'calendar__item--carriage': tournament.sportCode === 203,
                    'calendar__item': true
                  })
                }
                key={ tournament.eventCode }
                onClick={ handleOpenEvent(eventType === 'racing' ? tournament.meetingInfos?.meetingCode : tournament.code, tournament.eventCode) }
              >
                <span className="calendar__name">
                  {
                    calendarType !== 'all' || eventType !== 'numbers' ?
                      tournament.meetingInfos?.name ? tournament.meetingInfos?.name : tournament.name
                      :
                      tournament.meetingName
                  }
                </span>
                {
                  tournament.meetingInfos && calendarType !== 'all' && <div className="calendar__info--sum">
                    <span className="calendar__info--sum">
                      { `${ tournament.meetingInfos?.nextRaceNo } / ${ tournament.meetingInfos?.noRaces }` }
                    </span>
                  </div>
                }
                <div className="calendar__info">
                  <span className="calendar__icon--time">{ formattedDate }</span>
                </div>
              </li>
            );
          })
        }
      </ul>
    </>
  );
}

export default CalendarEvents;
